// Utils
import type { List } from 'immutable';
import { v4 as uuidv4 } from 'uuid';

import { chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import type { Goal } from 'app-components/flux/goals/goalsTypes';

import type { TransactionItem } from '../transactions/transactionsTypes';
import { mkSplitItem, mkTransactionItem } from '../transactions/transactionsTypes';
import type {
  BillsFilterType,
  ExpansionPanel,
  ExpansionPanelItem,
  ExpansionPanelSubsection,
  GSFStage,
  PlannedSpendingCardData,
  PlannedSpendingItem,
} from './freeToSpendTypes';
import {
  BillsFilterTypeEnum,
  ExpansionPanelItemTypeEnum,
  ExpansionPanelTypeEnum,
  GSF_STAGE_ORDER,
  GSFStageEnum,
} from './freeToSpendTypes';

// activeStage: The current phase of the GSF
// query: The phase you want to check is complete or not
// skipGettingStartedFlow: If the user has gone through an older version of the getting started flow, they should ALWAYS be completed with the new GSF
export const isStageComplete = (activeStage: GSFStage, query: GSFStage, skipGettingStartedFlow: boolean): boolean => {
  return (
    GSF_STAGE_ORDER.indexOf(activeStage) > GSF_STAGE_ORDER.indexOf(query) ||
    activeStage === GSFStageEnum.COMPLETE ||
    skipGettingStartedFlow
  );
};

export const getPlannedSpendingCardData = (
  plannedItem: PlannedSpendingItem,
  isPreviousMonth: boolean,
): PlannedSpendingCardData => {
  const target = (plannedItem.overwrittenTargetAmount ?? plannedItem.targetAmount) || 0;
  const calculatedSpentAmount = plannedItem.calculatedSpentAmount || 0;
  const leftToSpend = target + calculatedSpentAmount;
  const overSpent = leftToSpend < 0;
  const showUnused = isPreviousMonth && Math.abs(calculatedSpentAmount) < Math.abs(target);
  return { ...plannedItem, target, calculatedSpentAmount, leftToSpend, overSpent, showUnused };
};

export const getWidthPercent = (spent: number, plannedSpend: number, rollover: number) => {
  let widthPercent = 0;
  let widthPercentText = 0;
  let rolloverPercent = 0;
  let leftPercent = 0;
  let originalRolloverPercent = 0;

  const safeSpent = !Number.isNaN(spent) ? spent : 0;
  const safePlannedSpend = !Number.isNaN(plannedSpend) ? plannedSpend : 0;
  const safeRollover = !Number.isNaN(rollover) ? rollover : 0;

  if (safeRollover > 0) {
    // Calculate spent %
    if (safeSpent > 0 && Math.abs(safeSpent) < Math.abs(safePlannedSpend) + safeRollover) {
      widthPercent = (Math.abs(safeSpent) / (Math.abs(safePlannedSpend) + safeRollover)) * 100;
    }
    if (Math.abs(safeSpent) >= Math.abs(plannedSpend) + safeRollover) {
      widthPercent = 100;
    }

    widthPercentText = (Math.abs(safeSpent) / (Math.abs(safePlannedSpend) + safeRollover)) * 100;

    if (safeSpent <= 0) {
      widthPercentText = 0;
      widthPercent = 0;
    }

    // Calculate rollover %
    rolloverPercent = (Math.abs(safeRollover) / (safePlannedSpend + Math.abs(safeRollover))) * 100;
    originalRolloverPercent = rolloverPercent;
    if (100 - rolloverPercent - widthPercent <= 0) {
      rolloverPercent = 100 - widthPercent;
    }

    // Calculate Left %
    leftPercent = 100 - rolloverPercent - widthPercent;
  } else if (safeRollover < 0) {
    // Calculate rollover %
    rolloverPercent = (Math.abs(safeRollover) / safePlannedSpend) * 100;
    originalRolloverPercent = rolloverPercent;

    // Calculate spent %
    if (safeSpent > 0 && Math.abs(safeSpent) + Math.abs(safeRollover) < Math.abs(safePlannedSpend)) {
      widthPercent = (Math.abs(safeSpent) / Math.abs(safePlannedSpend)) * 100;
    }
    if (Math.abs(safeSpent) + Math.abs(safeRollover) >= Math.abs(plannedSpend)) {
      widthPercent = 100 - rolloverPercent; // Can become negative
    }

    widthPercentText = ((Math.abs(safeSpent) + Math.abs(safeRollover)) / Math.abs(safePlannedSpend)) * 100;

    if (safeSpent <= 0) {
      // We want to show the text for what it is if rollovers fill up the entire bar!
      if (rolloverPercent < 100) {
        widthPercentText = 0;
      }
      widthPercent = 0;
    }
    if (widthPercent < 0) {
      widthPercent = 0;
    }

    // Calculate Left %
    leftPercent = 100 - rolloverPercent - widthPercent;
  } else {
    if (safeSpent > 0 && Math.abs(safeSpent) < Math.abs(safePlannedSpend)) {
      widthPercent = (Math.abs(safeSpent) / Math.abs(safePlannedSpend)) * 100;
    }
    if (Math.abs(safeSpent) >= Math.abs(plannedSpend)) {
      widthPercent = 100;
    }
    if (safeSpent < 0) {
      widthPercent = 0;
    }
    widthPercentText = (Math.abs(safeSpent) / Math.abs(safePlannedSpend)) * 100;
    leftPercent = 100 - widthPercent;
    rolloverPercent = 0;
  }

  return {
    originalRolloverPercent,
    leftPercent,
    widthPercent,
    // We want to show the text if the negative rollover fills up the bar completely!
    widthPercentText: widthPercent > 7.9 || (rolloverPercent >= 100 && safeRollover < 0) ? widthPercentText : null,
    rolloverPercent,
  };
};

export const getAllMatchedTransactionArrays = (transferTransactionsItems: List<TransactionItem> | undefined) => {
  const retObj: Record<string, TransactionItem[]> = {};
  const matchedArrayIds: string[] = [];

  const isTransferTxnItem = (txnItem: TransactionItem | undefined): boolean => {
    const item = txnItem?.item;
    const splitItem = txnItem?.splitItem;
    if (!item) return false;

    const hasTransferLink = Boolean(
      item.transfer?.id || item.transfer?.clientId || splitItem?.transfer?.id || splitItem?.transfer?.clientId,
    );
    if (hasTransferLink) return true;

    const coa = item.coa ?? splitItem?.coa;
    return Boolean(chartOfAccountsUtils.isTransferCOA(coa));
  };

  transferTransactionsItems?.forEach((txnItem) => {
    if (Object.values(retObj).length + matchedArrayIds.length !== transferTransactionsItems?.size) {
      // check if we have paired all txns and no longer need to search
      const transactionItem = txnItem.item;
      const splitItem = txnItem.splitItem;
      const transactionItemId = transactionItem.id;
      if (transactionItemId && !matchedArrayIds.includes(transactionItemId)) {
        if (!(transactionItemId in retObj)) {
          retObj[transactionItemId] = [];
        }
        retObj[transactionItemId].push(txnItem);
        if (!isTransferTxnItem(txnItem)) {
          return;
        }
        let otherSideOfTransfer = transferTransactionsItems?.find((transferTxn) => {
          if (!isTransferTxnItem(transferTxn)) return false;

          const linkedByTransferId =
            transferTxn.item.transfer?.id === transactionItemId || transferTxn.item.id === transactionItem.transfer?.id;

          // Fallback when `transfer.id` isn't present (common for unmatched transfers):
          // pair by swapped accounts + equal/opposite amounts + same postedOn.
          const heuristicMatch =
            transactionItem.coa?.id &&
            transferTxn.item.coa?.id &&
            transactionItem.coa.id === transferTxn.item.accountId &&
            transferTxn.item.coa.id === transactionItem.accountId &&
            transferTxn.item.amount === transactionItem.amount * -1 &&
            transactionItem.postedOn === transferTxn.item.postedOn;

          return Boolean(linkedByTransferId || heuristicMatch);
        });
        let isSyntheticOtherSide = false;
        if (
          !otherSideOfTransfer &&
          splitItem?.coa?.type === 'ACCOUNT' &&
          transactionItem.coa?.id &&
          transactionItem.accountId &&
          transactionItem.amount
        ) {
          const syntheticClientId = uuidv4().toUpperCase();
          const isExcludedFromF2S = splitItem.isExcludedFromF2S ?? transactionItem.isExcludedFromF2S;
          const isExcludedFromReports = splitItem.isExcludedFromReports ?? transactionItem.isExcludedFromReports;
          const syntheticSplitItem = mkSplitItem({
            ...splitItem,
            amount: Number(transactionItem.amount) * -1,
            coa: { id: transactionItem.accountId, type: 'ACCOUNT' },
            transfer: { id: transactionItemId },
            isExcludedFromF2S,
            isExcludedFromReports,
          });
          isSyntheticOtherSide = true;
          otherSideOfTransfer = mkTransactionItem({
            transaction: txnItem.transaction,
            splitItem: syntheticSplitItem,
            item: {
              ...transactionItem,
              id: undefined,
              clientId: syntheticClientId,
              accountId: transactionItem.coa.id,
              coa: { id: transactionItem.accountId, type: 'ACCOUNT' },
              amount: Number(transactionItem.amount) * -1,
              transfer: { id: transactionItemId },
              isExcludedFromF2S,
              isExcludedFromReports,
            },
          });
        }
        const otherSideKey = otherSideOfTransfer?.item.id ?? otherSideOfTransfer?.item.clientId;
        if (otherSideOfTransfer && otherSideKey && !(otherSideKey in retObj)) {
          if (otherSideOfTransfer.item.amount > 0) {
            retObj[transactionItemId].push(otherSideOfTransfer);
          } else {
            retObj[transactionItemId].unshift(otherSideOfTransfer);
          }
          if (!isSyntheticOtherSide && otherSideOfTransfer.item.id) {
            matchedArrayIds.push(otherSideOfTransfer.item.id);
          }
        }
      }
    }
  });
  return Object.values(retObj);
};

const isTransfer = (txn: ExpansionPanelItem) => {
  if (txn?.type === ExpansionPanelItemTypeEnum.TRANSFER) {
    return true;
  } else if (txn?.type === ExpansionPanelItemTypeEnum.TXN) {
    // Prefer the combined item COA, but fall back to the splitItem COA.
    // Spending Plan can synthesize split rows where `item.coa` can be out of sync,
    // and we still want ACCOUNT/BA rows to land in the Transfers subsection.
    const txnItem = txn?.txnItem;
    const hasTransferLink = Boolean(txnItem?.splitItem?.transfer?.id || txnItem?.splitItem?.transfer?.clientId);
    if (hasTransferLink) return true;

    const coa = txnItem?.item?.coa ?? txnItem?.splitItem?.coa;
    return chartOfAccountsUtils.isTransferCOA(coa);
  }
  return false;
};

const normalizeTxns = (txns: TransactionItem[] | TransactionItem[][] | undefined) => {
  if (!txns) return;

  return Object.values(txns).map((txnArr) => {
    let sumAmount = 0;
    txnArr?.forEach((txnItem: TransactionItem) => {
      if (!txnItem.item.isExcludedFromF2S) {
        sumAmount = sumAmount + txnItem.item.amount;
      }
    });

    if (txnArr?.length > 1) {
      return { type: ExpansionPanelItemTypeEnum.TRANSFER, txnItemArray: txnArr, amount: sumAmount };
    } else {
      return { type: ExpansionPanelItemTypeEnum.TXN, txnItem: txnArr[0], amount: sumAmount };
    }
  });
};

const matchArraysAndNormalize = (txns: List<TransactionItem> | undefined): ExpansionPanelItem[] | undefined => {
  if (!txns) return;
  const allTxns: TransactionItem[] | TransactionItem[][] | undefined = getAllMatchedTransactionArrays(txns);
  if (!allTxns) return;

  const normalizedTxns: ExpansionPanelItem[] | undefined = normalizeTxns(allTxns);
  return normalizedTxns;
};

const filterByType = (normalizedTxns: ExpansionPanelItem[], filter: BillsFilterType | undefined) => {
  if (filter === BillsFilterTypeEnum.BILLS) {
    normalizedTxns = normalizedTxns.filter(
      (txn) => txn.type === ExpansionPanelItemTypeEnum.TXN && !txn.txnItem.item.isSubscription && !isTransfer(txn),
    );
  } else if (filter === BillsFilterTypeEnum.SUBSCRIPTIONS) {
    normalizedTxns = normalizedTxns.filter(
      (txn) => txn.type === ExpansionPanelItemTypeEnum.TXN && txn.txnItem.item.isSubscription && !isTransfer(txn),
    );
  } else if (filter === BillsFilterTypeEnum.TRANSFERS) {
    normalizedTxns = normalizedTxns.filter(
      (txn) => txn.type === ExpansionPanelItemTypeEnum.TRANSFER || isTransfer(txn),
    );
  }
  return normalizedTxns;
};

export const getNormalizedFilteredTxns = (
  txns: List<TransactionItem> | undefined,
  filter: BillsFilterType | undefined,
) => {
  const filteredTxns: ExpansionPanelItem[] | undefined = matchArraysAndNormalize(txns);
  if (!filteredTxns) return;
  return filterByType(filteredTxns, filter);
};

const getTxnQuantity = (txns: ExpansionPanelItem[] | undefined) =>
  (txns ?? []).reduce((size: number, txn: ExpansionPanelItem) => {
    if (txn.type === ExpansionPanelItemTypeEnum.TXN) {
      return size + 1;
    } else if (txn.type === ExpansionPanelItemTypeEnum.TRANSFER) {
      return size + (txn.txnItemArray?.length ?? 2);
    }
    return size;
  }, 0) ?? 0;

const emptyExpansionPanel = (sortByType: boolean | undefined): ExpansionPanel => {
  if (sortByType) {
    const subsections: ExpansionPanelSubsection[] = [
      {
        type: 'bills',
        amount: 0,
      },
      {
        type: 'subscriptions',
        amount: 0,
      },
      {
        type: 'transfers',
        amount: 0,
      },
    ];

    return {
      type: ExpansionPanelTypeEnum.SORTED_BY_TYPE,
      items: subsections,
      quantity: 0,
    };
  } else {
    return {
      type: ExpansionPanelTypeEnum.UNSORTED,
      quantity: 0,
    };
  }
};

export const getNormalizedSortedTxns = (
  txns: List<TransactionItem> | undefined,
  sortByType?: boolean,
  filter?: BillsFilterType,
): ExpansionPanel => {
  let normalizedTxns: ExpansionPanelItem[] | undefined = matchArraysAndNormalize(txns);
  if (!normalizedTxns) return emptyExpansionPanel(sortByType);

  if (!sortByType) {
    normalizedTxns = filterByType(normalizedTxns, filter);

    const unsortedExpansionPanel: ExpansionPanel = {
      type: ExpansionPanelTypeEnum.UNSORTED,
      items: normalizedTxns,
      quantity: getTxnQuantity(normalizedTxns),
    };
    return unsortedExpansionPanel;
  }

  // Calculate the bills
  const bills: ExpansionPanelItem[] = normalizedTxns.filter(
    (txn) => txn.type === ExpansionPanelItemTypeEnum.TXN && !txn.txnItem.item.isSubscription && !isTransfer(txn),
  );
  const billsAmount = bills?.reduce((sum, item) => sum + item.amount, 0) ?? 0;

  const subscriptions: ExpansionPanelItem[] = normalizedTxns.filter(
    (txn) => txn.type === ExpansionPanelItemTypeEnum.TXN && txn.txnItem.item.isSubscription && !isTransfer(txn),
  );
  const subscriptionsAmount = subscriptions?.reduce((sum, item) => sum + item.amount, 0) ?? 0;

  const transfers: ExpansionPanelItem[] = normalizedTxns.filter(
    (txn) => txn.type === ExpansionPanelItemTypeEnum.TRANSFER || isTransfer(txn),
  );
  const transfersAmount = transfers?.reduce((sum, item) => sum + item.amount, 0) ?? 0;

  const subsections: ExpansionPanelSubsection[] = [
    {
      type: 'bills',
      items: bills,
      amount: billsAmount,
    },
    {
      type: 'subscriptions',
      items: subscriptions,
      amount: subscriptionsAmount,
    },
    {
      type: 'transfers',
      items: transfers,
      amount: transfersAmount,
    },
  ];

  const sortedExpansionPanel: ExpansionPanel = {
    type: ExpansionPanelTypeEnum.SORTED_BY_TYPE,
    items: subsections,
    quantity: getTxnQuantity(normalizedTxns),
  };

  return sortedExpansionPanel;
};

export const getNormalizedSortedGoals = (goals?: List<Goal> | undefined): ExpansionPanel => {
  const unsortedExpansionPanel: ExpansionPanel = {
    type: ExpansionPanelTypeEnum.GOALS,
    items: goals,
    quantity: goals?.size ?? 0,
  };
  return unsortedExpansionPanel;
};
